import { useState } from 'react';
import { Users, Plus, Pencil, Trash2, Check, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { usePortalRoles } from '@/hooks/react-query/usePortalRoles';
import { CONTACT_ROLE_OPTIONS } from '@/types/investor';
import type {
  DraftInvestorContact,
  InvestorContactCreateData,
  InvestorContactSearchResult,
} from '@/types/investor';
import { InvestorContactFormFields } from './InvestorContactFormFields';
import { ExistingContactPicker } from './ExistingContactPicker';
import { CopySourceNote } from './CopySourceNote';
import {
  contactFormDataFromExisting,
  contactFullName,
  isEmailTaken as isEmailInList,
} from './existingContactMapping';
import {
  EMPTY_CONTACT_FORM_DATA,
  validateContactForm,
  type InvestorContactFormErrors,
} from './investorContactFormValidation';

interface Props {
  contacts: DraftInvestorContact[];
  onChange: (contacts: DraftInvestorContact[]) => void;
}

type FormMode =
  | { kind: 'add' }
  | { kind: 'copy'; source: InvestorContactSearchResult }
  | { kind: 'edit'; index: number }
  | null;

const FORM_TITLES: Record<Exclude<FormMode, null>['kind'], string> = {
  add: 'New Contact',
  copy: 'New Contact',
  edit: 'Edit Contact',
};

const SAVE_LABELS: Record<Exclude<FormMode, null>['kind'], string> = {
  add: 'Add Contact',
  copy: 'Add Contact',
  edit: 'Update Contact',
};

export function PendingContactsSection({ contacts, onChange }: Props) {
  const [formMode, setFormMode] = useState<FormMode>(null);
  const [formData, setFormData] = useState<InvestorContactCreateData>(
    EMPTY_CONTACT_FORM_DATA
  );
  const [errors, setErrors] = useState<InvestorContactFormErrors>({});
  const [copyError, setCopyError] = useState<string | null>(null);

  const { data: portalRolesData } = usePortalRoles({ include_system: true });
  const portalRoles = portalRolesData?.items ?? [];

  const isFormOpen = formMode !== null;

  const isEmailTaken = (email?: string | null, ignoreIndex?: number) =>
    isEmailInList(
      contacts.filter((_, index) => index !== ignoreIndex),
      email
    );

  const openAddForm = () => {
    setFormData(EMPTY_CONTACT_FORM_DATA);
    setErrors({});
    setCopyError(null);
    setFormMode({ kind: 'add' });
  };

  // The form opens prefilled with a copy of the person so the details can be
  // reviewed and adjusted before they are added.
  const openCopyForm = (contact: InvestorContactSearchResult) => {
    if (isEmailTaken(contact.email)) {
      setCopyError(
        `${contactFullName(contact)} is already on this investor's contact list.`
      );
      return;
    }

    setFormData(contactFormDataFromExisting(contact));
    setErrors({});
    setCopyError(null);
    setFormMode({ kind: 'copy', source: contact });
  };

  const openEditForm = (index: number) => {
    setFormData(contacts[index]);
    setErrors({});
    setCopyError(null);
    setFormMode({ kind: 'edit', index });
  };

  const closeForm = () => {
    setFormMode(null);
    setErrors({});
  };

  const updateField = (
    field: keyof InvestorContactCreateData,
    value: string | boolean | null
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (field in errors) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleSave = () => {
    const newErrors = validateContactForm(formData);
    const editedIndex = formMode?.kind === 'edit' ? formMode.index : undefined;
    if (!newErrors.email && isEmailTaken(formData.email, editedIndex)) {
      newErrors.email =
        'Another contact on this investor already uses this email';
    }
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    if (formMode?.kind === 'edit') {
      const next = [...contacts];
      next[formMode.index] = {
        ...formData,
        _uiKey: contacts[formMode.index]._uiKey,
      };
      onChange(next);
    } else {
      onChange([...contacts, { ...formData, _uiKey: crypto.randomUUID() }]);
    }
    closeForm();
  };

  const handleRemove = (index: number) => {
    onChange(contacts.filter((_, i) => i !== index));
    // The removed contact may be the one the "already on this investor" warning names.
    setCopyError(null);
    if (formMode?.kind === 'edit' && formMode.index === index) {
      closeForm();
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Users className="h-5 w-5" />
            <span>Contacts</span>
          </div>
          {!isFormOpen && (
            <Button
              variant="outline"
              onClick={openAddForm}
              className="flex items-center space-x-2"
            >
              <Plus className="h-4 w-4" />
              <span>Add Contact</span>
            </Button>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {!isFormOpen && (
          <div className="space-y-2">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <div className="w-full sm:max-w-sm">
                <ExistingContactPicker onSelect={openCopyForm} />
              </div>
              <p className="text-sm text-muted-foreground">
                or use <span className="font-medium">Add Contact</span> to enter
                someone new.
              </p>
            </div>
            {copyError && <p className="text-sm text-red-600">{copyError}</p>}
          </div>
        )}

        {contacts.length === 0 && !isFormOpen && (
          <p className="text-muted-foreground text-center py-8">
            No contacts yet. Add a contact to get started.
          </p>
        )}
        {contacts.length > 0 && (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>Primary</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {contacts.map((contact, index) => (
                <TableRow key={contact._uiKey}>
                  <TableCell className="font-medium">
                    {contact.first_name} {contact.last_name}
                    {contact.title && (
                      <span className="text-muted-foreground ml-1">
                        ({contact.title})
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    {CONTACT_ROLE_OPTIONS[contact.role] || contact.role}
                  </TableCell>
                  <TableCell>{contact.email || '-'}</TableCell>
                  <TableCell>{contact.phone || '-'}</TableCell>
                  <TableCell>
                    {contact.is_primary && (
                      <Badge variant="secondary">
                        <Check className="h-3 w-3 mr-1" />
                        Primary
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openEditForm(index)}
                        disabled={isFormOpen}
                        title="Edit contact"
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemove(index)}
                        disabled={isFormOpen}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        title="Remove contact"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {formMode && (
          <div className="space-y-4 pt-4 border-t">
            <h3 className="text-lg font-semibold">
              {FORM_TITLES[formMode.kind]}
            </h3>
            {formMode.kind === 'copy' && (
              <CopySourceNote source={formMode.source} />
            )}
            <InvestorContactFormFields
              formData={formData}
              errors={errors}
              onChange={updateField}
              portalRoles={portalRoles}
            />
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={closeForm}
                className="flex items-center space-x-2"
              >
                <X className="h-4 w-4" />
                <span>Cancel</span>
              </Button>
              <Button
                onClick={handleSave}
                className="flex items-center space-x-2"
              >
                <Check className="h-4 w-4" />
                <span>{SAVE_LABELS[formMode.kind]}</span>
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
